import React, { useState, useRef, useEffect } from 'react';
import { CloudUpload, Download, LogIn, Zap, XCircle } from 'lucide-react';
import { useHistory } from 'react-router-dom';
import TradeInPricingTabs from '../../components/TradeInPricingTabs';
import AlertNotification from '../../components/AlertNotification';
import CalendarRangeInput from '../../components/CalendarRangeInput';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal';
import Pagination from '../../components/Pagination';
import apiHelper from '../../apiHelper';
import noBrandImage from '../../assets/img/brand/no-brand-img.png';

const YELLOW = '#FFEB00';
const CIRCLE = 35;
const PAGE_SIZE = 10;

// Django counts only the *non-file* parts of a multipart body against
// DATA_UPLOAD_MAX_MEMORY_SIZE and rejects the request with RequestDataTooBig
// before the view ever runs. A full Master List carries one issue entry per
// problem row, so a 5,000-row pricing update pushed these blobs past that budget
// and the upload 500'd — the larger the price change, the more certain the
// failure. Sent as a file part instead, the same JSON is exempt from that budget
// entirely, so the payload scales with the sheet rather than against a ceiling.
const appendJsonPart = (formData, name, value) => {
    const json = JSON.stringify(value);
    formData.append(name, new Blob([json], { type: 'application/json' }), `${name}.json`);
};

// `id` is a client-side key for the validation table; the server reads only
// row/issues/action. Dropping it keeps the blob to what the API actually uses.
const slimIssueRows = (rows) => rows.map(({ row, issues, action }) => ({ row, issues, action }));

// The upload can fail in ways that never reach Django: a proxy timeout or a killed
// gunicorn worker answers with its own HTML error page, which carries no
// Access-Control-Allow-Origin, so the browser blocks it and fetch rejects. None of
// those carry a `message`, and reporting every one of them as "missing Master List
// sheet" sent users hunting through a spreadsheet that was never the problem.
const uploadFailureModal = (res, errData) => {
    if (errData?.message) return { title: 'Upload failed', message: errData.message };
    const status = res?.status ?? 0;
    if (status === 0) return {
        title:   'Could not reach the server',
        message: 'The validation request never completed — it most likely timed out on the server. Please try again, and if it keeps happening tell IT that the trade-in pricing upload is timing out.',
    };
    if (status === 413) return {
        title:   'File too large',
        message: 'The server rejected the file for being too large. Please tell IT that the trade-in pricing upload is hitting the upload size limit.',
    };
    if (status === 401 || status === 403) return {
        title:   'Session expired',
        message: 'Your session is no longer valid. Please log in again and retry the upload.',
    };
    if (status >= 500) return {
        title:   'Server error while validating',
        message: `The server could not finish validating this file (HTTP ${status}). A large Master List can exceed the server's time limit — please try again, and tell IT if it persists.`,
    };
    return {
        title:   'Upload failed',
        message: `The server rejected the upload (HTTP ${status}). Please re-download the template and try again.`,
    };
};

const PRICING_STEPS = [
    { num: '01', label: 'Download and Upload' },
    { num: '02', label: 'Data Validation' },
    { num: '03', label: 'Complete' },
];

function PricingStepper({ currentStep }) {
    return (
        <div style={{
            display: 'flex', alignItems: 'flex-start', padding: '20px 100px',
            marginBottom: '12px', borderRadius: '8px', background: 'white', boxSizing: 'border-box',
        }}>
            {PRICING_STEPS.map((step, idx) => {
                const isActive = idx + 1 === currentStep;
                const isDone   = idx + 1 < currentStep;
                const leftBlack  = idx + 1 <= currentStep;
                const rightBlack = idx + 2 <= currentStep;
                const lineBackground =
                    leftBlack && rightBlack ? '#212529'
                    : leftBlack  ? 'linear-gradient(to right, #212529 50%, #CED4DA 50%)'
                    : rightBlack ? 'linear-gradient(to right, #CED4DA 50%, #212529 50%)'
                    : '#CED4DA';
                return (
                    <React.Fragment key={step.num}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, width: CIRCLE, overflow: 'visible', flexShrink: 0 }}>
                            <div style={{
                                width: CIRCLE, height: CIRCLE, borderRadius: '50%',
                                background: isActive ? YELLOW : isDone ? YELLOW : 'transparent',
                                border: isActive || isDone ? 'none' : '1px solid #CED4DA',
                                color: isActive ? '#000' : isDone ? '#000' : '#CED4DA',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontWeight: 600, fontSize: 16, fontFamily: 'Montserrat',
                                boxSizing: 'border-box', flexShrink: 0,
                            }}>
                                {step.num}
                            </div>
                            <span style={{
                                fontSize: 16, fontWeight: 600, fontFamily: 'Montserrat',
                                color: isActive || isDone ? '#212529' : '#6C757D',
                                whiteSpace: 'nowrap', textAlign: 'center',
                            }}>
                                {step.label}
                            </span>
                        </div>
                        {idx < PRICING_STEPS.length - 1 && (
                            <div style={{ flex: 1, height: '1px', background: lineBackground, alignSelf: 'flex-start', marginTop: CIRCLE / 2 }} />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );
}

export default function InitiatePricing() {
    const history = useHistory();
    const allowNavigationRef = useRef(false);
    const [discardModalOpen, setDiscardModalOpen] = useState(false);
    const [discardSource, setDiscardSource]       = useState(null);
    const [nextLocation, setNextLocation]         = useState(null);
    const [currentStep, setCurrentStep]           = useState(1);

    // Step 1 state
    const [uploadedFile, setUploadedFile]         = useState(null);
    const [isDragging, setIsDragging]             = useState(false);
    const [pricingType, setPricingType]           = useState('normal');
    const [isDownloading, setIsDownloading]       = useState(false);
    const [isDownloadHovered, setIsDownloadHovered] = useState(false);
    const [uploadError, setUploadError]           = useState(null);
    const fileInputRef = useRef(null);
    // Ref, not the isScanning state: two clicks landing in the same tick both read the
    // pre-render value, and the button's disabled prop only lands once React re-renders.
    // Two concurrent uploads of the same Master List also compete for CPU server-side,
    // which is what pushed both of them past the request timeout.
    const scanInFlightRef = useRef(false);

    // Step 2 state
    const [validationStatus, setValidationStatus] = useState('invalid');
    const [activeFilter, setActiveFilter]         = useState('Warning');
    const [validationPage, setValidationPage]     = useState(1);
    const [validationSortField, setValidationSortField] = useState('row');
    const [validationSortOrder, setValidationSortOrder] = useState('asc');
    const [warningRows, setWarningRows]           = useState([]);
    const [criticalRows, setCriticalRows]         = useState([]);
    const [fatalRows, setFatalRows]               = useState([]);
    const [notes, setNotes]                       = useState('');
    const [isScanning, setIsScanning]             = useState(false);
    const [formError, setFormError]               = useState('');
    const [fieldErrors, setFieldErrors]           = useState({});
    const [campaignName, setCampaignName]         = useState('');
    const [startTime, setStartTime]               = useState('');
    const [endTime, setEndTime]                   = useState('');
    const [summaryData, setSummaryData]           = useState({
        finalPricingRows: 0, fileTotalRows: 0, fileUpdatedRows: 0, skippedRows: 0,
    });
    const [isValidationDownloading, setIsValidationDownloading] = useState(false);
    const [isCompleting, setIsCompleting]         = useState(false);
    const [completeError, setCompleteError]       = useState('');
    const [notification, setNotification]         = useState({ show: false, type: '', recordName: '', action: '', message: '' });
    const [errorModal, setErrorModal]             = useState({ show: false, title: '', message: '' });
    const [campaignTimeModal, setCampaignTimeModal] = useState({ show: false });
    const [campaignEndTimeModal, setCampaignEndTimeModal] = useState({ show: false });
    const [updatedRowNums, setUpdatedRowNums] = useState([]);

    // ── All useEffects at top level ───────────────────────────────────────────
    const isDirty = !!(uploadedFile || pricingType !== 'normal' || campaignName || startTime || endTime);

    useEffect(() => {
        if (!isDirty) return;
        const handleBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    useEffect(() => {
        const unblock = history.block((tx) => {
            if (!isDirty) return true;
            if (allowNavigationRef.current) { allowNavigationRef.current = false; return true; }
            setDiscardSource('navigate');
            setNextLocation(tx);
            setDiscardModalOpen(true);
            return false;
        });
        return () => unblock();
    }, [isDirty]);

    useEffect(() => {
        // placeholder — keeps hook order stable if scanning logic is re-enabled
    }, [currentStep]);

    useEffect(() => {

        const skippedRowNums = new Set([
            ...warningRows.filter(r => r.action === 'skip').map(r => r.row),
            ...criticalRows.filter(r => r.action === 'skip').map(r => r.row),
            ...fatalRows.map(r => r.row),
        ]);

        setSummaryData(prev => ({
            ...prev,
            skippedRows: skippedRowNums.size,
            // Only rows the upload actually changed can be priced, and only the
            // ones the user kept. Subtracting the skip count outright charged a
            // skip of a row that carried no change — a pre-existing issue the
            // file did not touch — against the rows that did carry one.
            finalPricingRows: updatedRowNums.filter(r => !skippedRowNums.has(r)).length,
        }));
    }, [warningRows, criticalRows, fatalRows, updatedRowNums]);
        

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleBrowseClick = () => fileInputRef.current && fileInputRef.current.click();
    const handleDragOver    = (e) => e.preventDefault();
    const handleDragEnter   = (e) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave   = (e) => { e.preventDefault(); setIsDragging(false); };
    const handleDrop        = (e) => { e.preventDefault(); setIsDragging(false); processFile(e.dataTransfer.files[0]); };
    const handleFileChange  = (e) => processFile(e.target.files[0]);

    const processFile = (file) => {
        if (!file) return;
        setUploadError(null);
        if (!/\.(xls|xlsx)$/i.test(file.name)) {
            setUploadError('Upload failed — Only Excel (.xlsx) file below 100MB is supported');
            return;
        }
        setUploadedFile(file);
        setFieldErrors((prev) => ({ ...prev, uploadedFile: '' }));
    };

    // Everything step 2 renders comes from one upload's response, so it is cleared as a
    // unit — a failed or superseded upload must not leave the previous run's verdict up.
    const resetValidationResults = () => {
        setValidationStatus('invalid');
        setWarningRows([]);
        setCriticalRows([]);
        setFatalRows([]);
        setUpdatedRowNums([]);
        setSummaryData({ finalPricingRows: 0, fileTotalRows: 0, fileUpdatedRows: 0, skippedRows: 0 });
    };

    const handleRemoveFile = () => {
        setUploadedFile(null);
        setUploadError(null);
        if (fileInputRef.current) fileInputRef.current.value = null;
    };

    const handleDiscard = () => { setDiscardSource('discard'); setDiscardModalOpen(true); };

    const handleCancelDiscard = () => {
        setDiscardModalOpen(false);
        setNextLocation(null);
        setDiscardSource(null);
    };

    const handleConfirmDiscard = () => {
        setUploadedFile(null);
        setUploadError(null);
        setPricingType('normal');
        setCampaignName('');
        setStartTime('');
        setEndTime('');
        setCurrentStep(1);
        setFormError('');
        setFieldErrors({});
        setValidationStatus('invalid');
        setWarningRows([]);
        setCriticalRows([]);
        setFatalRows([]);
        setNotes('');
        setCompleteError('');
        setUpdatedRowNums([]);
        setSummaryData({ finalPricingRows: 0, fileTotalRows: 0, fileUpdatedRows: 0, skippedRows: 0 });
        if (fileInputRef.current) fileInputRef.current.value = null;
        allowNavigationRef.current = true;
        setDiscardModalOpen(false);
        if (discardSource === 'navigate' && nextLocation) {
            history.push(nextLocation.pathname, nextLocation.state || {});
        }
        setDiscardSource(null);
        setNextLocation(null);
    };

    const closeErrorModal = () => {
        setErrorModal({ show: false, title: '', message: '' });
        setCurrentStep(1);
    };

    const handleDownload = async () => {
        if (isDownloading) return;
        setIsDownloading(true);
        try {
            const res = await apiHelper('sales/download-trade-in-listings/', 'GET');
            if (res && res.ok) {
                const blob = await res.blob();
                const url  = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url; link.download = 'Listing Items.xlsx'; link.click();
            }
        } catch (err) {
            console.error('Download error:', err);
        } finally {
            setIsDownloading(false);
        }
    };

    const handleBack = () => { setCurrentStep((s) => s - 1); setValidationPage(1); };

    const handleNext = async () => {
        if (currentStep === 1) {
            if (scanInFlightRef.current) return;
            const errs = {};
            if (!uploadedFile) errs.uploadedFile = 'Upload File is required';
            if (!pricingType)  errs.pricingType  = 'This field is required!';
            if (pricingType === 'promotional') {
                if (!campaignName.trim())   errs.campaignName      = 'Campaign Name is required';
                if (!startTime || !endTime) errs.promotionalPeriod = 'Start Time and End Time are required';
            }
            if (Object.keys(errs).length > 0) {
                setFormError('Please fix the errors below');
                setFieldErrors(errs);
                return;
            }
            setFormError('');
            setFieldErrors({});

            const formData = new FormData();
            formData.append('file', uploadedFile);
            formData.append('pricing_type', pricingType);
            if (pricingType === 'promotional') {
                formData.append('campaign_name', campaignName);
                formData.append('start_time', toSGT(startTime));
                formData.append('end_time',   toSGT(endTime));
            }

        
            scanInFlightRef.current = true;
            // Set before the campaign-name check rather than after: that check is a full
            // round-trip during which Next stayed enabled, which was the widest window a
            // double-click had to fire a second upload.
            setIsScanning(true);

            try {

                if (pricingType === 'promotional') {
                    const checkRes = await apiHelper(
                        `sales/check-campaign-name/?name=${encodeURIComponent(campaignName)}`,
                        'GET'
                    );
                    if (checkRes && checkRes.ok) {
                        const checkData = await checkRes.json();
                        if (checkData.campaign_exists) {
                            setFieldErrors((prev) => ({ ...prev, campaignName: `Campaign Name already exists` }));
                            setFormError('Please fix the errors below');
                            setIsScanning(false);
                            setCurrentStep(1);
                            return;
                        }
                    }
                }

                setCurrentStep(2);

                const res = await apiHelper('sales/upload-trade-in-pricing-excel/', 'POST', formData, 'multipart/form-data');

                if (!res || !res.ok) {
                    const errData = await res?.json().catch(() => null);
                    setErrorModal({ show: true, ...uploadFailureModal(res, errData) });
                    // What step 2 is showing belongs to a file that is now gone, and the
                    // "All Rows are Valid" panel does not gate on the modal — leaving it
                    // up offered a "proceed to the next step" screen for an upload that never
                    // validated. Step 1 is the only honest place to land once the file is gone.
                    resetValidationResults();
                    handleRemoveFile();
                    setCurrentStep(1);
                    return;
                }

                const data      = await res.json();
                const hasIssues = (data.warnings?.length || 0) + (data.criticals?.length || 0) + (data.fatals?.length || 0) > 0;
                setValidationStatus(hasIssues ? 'invalid' : 'valid');
                // Set before the issue lists — the summary effect reads it, and a
                // render carrying the new issue rows against the previous file's
                // row numbers would price the wrong rows.
                setUpdatedRowNums(data.updated_row_nums || []);
                setWarningRows((data.warnings  || []).map((w, i) => ({ ...w, id: i + 1 })));
                setCriticalRows((data.criticals || []).map((c, i) => ({ ...c, id: i + 1 })));
                setFatalRows((data.fatals    || []).map((f, i) => ({ ...f, id: i + 1 })));
                setSummaryData({
                    fileTotalRows:    data.total_rows   || 0,
                    fileUpdatedRows:  data.updated_rows || 0,
                    skippedRows:      data.skipped_rows || 0,
                    finalPricingRows: data.updated_rows   || 0,
                    validRows:        data.valid_rows   || 0,
                });

                // Auto-select most severe tab that has rows
                if ((data.fatals    || []).length > 0) setActiveFilter('Fatal');
                else if ((data.criticals || []).length > 0) setActiveFilter('Critical');
                else if ((data.warnings  || []).length > 0) setActiveFilter('Warning');

            } catch (err) {
                console.error('Upload error:', err);
                setFormError('Something went wrong. Please try again.');
            } finally {
                scanInFlightRef.current = false;
                setIsScanning(false);
            }
            return;
        }
        setCurrentStep((s) => s + 1);
    };

    const handleValidationDownload = async () => {
        if (isValidationDownloading) return;
        setIsValidationDownloading(true);
        try {
            const formData = new FormData();
            formData.append('file', uploadedFile);
            appendJsonPart(formData, 'rows_data', {
                warnings:  slimIssueRows(warningRows),
                criticals: slimIssueRows(criticalRows),
                fatals:    slimIssueRows(fatalRows),
            });
            const res = await apiHelper('sales/download-trade-in-pricing-validation/', 'POST', formData, 'multipart/form-data');
            if (res && res.ok) {
                const blob = await res.blob();
                const url  = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url; link.download = 'Validation_Result.xlsx'; link.click();
                window.URL.revokeObjectURL(url);
            }
        } catch (err) {
            console.error('Validation download error:', err);
        } finally {
            setIsValidationDownloading(false);
        }
    };


    const toISOTime = (dtStr) => {
        if (!dtStr) return '';
        const d = (dtStr instanceof Date) ? dtStr : new Date(String(dtStr).trim().replace(' ', 'T'));
        if (isNaN(d.getTime())) return String(dtStr);
        const pad = n => String(n).padStart(2, '0');
        // Interpret displayed hours as SGT so the "already started/ended" check is in SGT
        return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00+08:00`;
    };

    // ── Check campaign start time before completing ────────────────────────
    const handleCompleteClick = () => {
        if (pricingType === 'promotional' && startTime && endTime) {
            
            const now   = new Date();
            const start = new Date(toISOTime(startTime));
            const end   = new Date(toISOTime(endTime));

            // End time already passed — block save
            if (end < now) {
                setCampaignEndTimeModal({ show: true });
                return;
            }

            // Start time already passed — warn but allow
            if (start < now) {
                setCampaignTimeModal({ show: true });
                return;
            }
        }
        handleComplete();
    };


    const handleComplete = async () => {
        if (isCompleting) return;
        setIsCompleting(true);
        setCompleteError('');
        setCampaignTimeModal({ show: false });

        try {
            const formData = new FormData();
            formData.append('file', uploadedFile);
            formData.append('pricing_type', pricingType);
            formData.append('notes', notes);
            appendJsonPart(formData, 'warnings',  slimIssueRows(warningRows));
            appendJsonPart(formData, 'criticals', slimIssueRows(criticalRows));
            appendJsonPart(formData, 'fatals',    slimIssueRows(fatalRows));
            appendJsonPart(formData, 'updated_row_nums', updatedRowNums);
            
            if (pricingType === 'promotional') {
                formData.append('campaign_name', campaignName);
                formData.append('start_time', toSGT(startTime));
                formData.append('end_time',   toSGT(endTime));
            }
            appendJsonPart(formData, 'rows_decision', {
                keep_rows: [
                    ...warningRows.filter(r => r.action === 'keep').map(r => r.row),
                    ...criticalRows.filter(r => r.action === 'keep').map(r => r.row),
                ],
                skip_rows: [
                    ...warningRows.filter(r => r.action === 'skip').map(r => r.row),
                    ...criticalRows.filter(r => r.action === 'skip').map(r => r.row),
                    ...fatalRows.map(r => r.row),
                ],
            });

            const endpoint = pricingType === 'promotional'
                ? 'sales/complete-trade-in-promotional-pricing/'
                : 'sales/complete-trade-in-pricing/';

            const res = await apiHelper(endpoint, 'POST', formData, 'multipart/form-data');
            if (!res || !res.ok) {
                const errData = await res?.json().catch(() => null);
                setCompleteError(errData?.message || 'Failed to complete pricing. Please try again.');
                return;
            }
            const data = await res.json();
            console.log('[WCM sync] payload sent:', data.wcm_sync);
            handleConfirmDiscard();
            // console.log("00000000000000000000000000000000000000")
            console.log(pricingType)

            if (pricingType === 'promotional') {
                history.push('/trade-in-pricing/promotional-campaign', { successMessage: 'Promotional Pricing Saved Successfully' });
            }
            else{
                history.push('/trade-in-pricing/pricing-history', { successMessage: 'Normal Pricing Saved Successfully' });
            }
            
        } catch (err) {
            console.error('Complete error:', err);
            setCompleteError('Something went wrong. Please try again.');
        } finally {
            setIsCompleting(false);
        }
    };

    const currentFilterRows = activeFilter === 'Warning' ? warningRows : activeFilter === 'Critical' ? criticalRows : fatalRows;
    const isFatal      = activeFilter === 'Fatal';

    const handleValidationSort = (field) => {
        if (validationSortField === field) {
            setValidationSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setValidationSortField(field);
            setValidationSortOrder('asc');
        }
        setValidationPage(1);
    };

    const sortedFilterRows = [...currentFilterRows].sort((a, b) => {
        let aVal = a[validationSortField];
        let bVal = b[validationSortField];
        if (validationSortField === 'issues') {
            aVal = Array.isArray(aVal) ? aVal.join(' ') : (aVal ?? '');
            bVal = Array.isArray(bVal) ? bVal.join(' ') : (bVal ?? '');
            return validationSortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        }
        aVal = aVal ?? 0;
        bVal = bVal ?? 0;
        return validationSortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });

    const totalPages   = Math.ceil(sortedFilterRows.length / PAGE_SIZE);
    const pagedRows    = sortedFilterRows.slice((validationPage - 1) * PAGE_SIZE, validationPage * PAGE_SIZE);
    const warningKeep  = warningRows.filter(r => r.action === 'keep').length;
    const warningSkip  = warningRows.filter(r => r.action === 'skip').length;
    const criticalKeep = criticalRows.filter(r => r.action === 'keep').length;
    const criticalSkip = criticalRows.filter(r => r.action === 'skip').length;

    const updateRowAction = (id, action) => {
        if (activeFilter === 'Warning')  setWarningRows(prev  => prev.map(r => r.id === id ? { ...r, action } : r));
        if (activeFilter === 'Critical') setCriticalRows(prev => prev.map(r => r.id === id ? { ...r, action } : r));
    };

    const handleKeepAll = () => {
        if (activeFilter === 'Warning')  setWarningRows(prev  => prev.map(r => ({ ...r, action: 'keep' })));
        if (activeFilter === 'Critical') setCriticalRows(prev => prev.map(r => ({ ...r, action: 'keep' })));
    };

    const handleFilterChange = (filter) => { setActiveFilter(filter); setValidationPage(1); setValidationSortField('row'); setValidationSortOrder('asc'); };

    // Stamps the displayed hours/minutes as Singapore time (+08:00), regardless of browser timezone.
    // Whatever the user sees in the picker is treated as SGT.
    const toSGT = (date) => {
        if (!date) return '';
        const d = (date instanceof Date) ? date : new Date(date);
        if (isNaN(d.getTime())) return String(date);
        const pad = n => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00+08:00`;
    };

    // ── Derived flags ─────────────────────────────────────────────────────────
    const isDisabled       = isScanning || errorModal.show || campaignTimeModal.show || campaignEndTimeModal.show;
    const hasUploadError   = !!(fieldErrors.uploadedFile || uploadError);

    // ── Shared button style helper ────────────────────────────────────────────
    const btnBase = {
        borderRadius: '8px', boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)',
        display: 'flex', height: '34px', minWidth: '100px', padding: '10px',
        justifyContent: 'center', alignItems: 'center', gap: '10px',
        fontSize: '12px', fontWeight: 600,
    };

    const disabledStyle   = { border: '1px solid #ADB5BD', background: '#E9ECEF', color: '#ADB5BD' };
    const discardStyle    = isDisabled ? disabledStyle : { border: '1px solid #212529', background: '#FFF', color: '#212529' };
    const backStyle       = isDisabled ? disabledStyle : { border: '1px solid #212529', background: '#FFF', color: '#212529' };
    const nextStyle       = isDisabled ? disabledStyle : { border: 'inherit', background: '#000', color: 'white' };
    const completeDisabled = isCompleting || summaryData.finalPricingRows === 0 || errorModal.show;
    const completeStyle   = completeDisabled ? disabledStyle : { border: 'inherit', background: '#000', color: 'white' };

    return (
        <div className="company-body new-look stock-wrap bg-checkbox">
            <div className="employee-tabs-container">
                <TradeInPricingTabs />
                <PricingStepper currentStep={currentStep} />

                {notification.show && (
                    <AlertNotification
                        type={notification.type}
                        recordName={notification.recordName || ''}
                        action={notification.action || 'create'}
                        message={notification.message}
                        onClose={() => setNotification({ show: false, type: '', recordName: '', action: '', message: '' })}
                    />
                )}

                {/* ── Card Header ── */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '20px', background: '#FFF', borderRadius: '12px',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.10)', marginBottom: '12px',
                    pointerEvents: isDisabled ? 'none' : 'auto',
                }}>
                    <h4 style={{ margin: 0, fontFamily: 'Montserrat', fontWeight: 700, fontSize: 20, color: '#212529', lineHeight: 'normal' }}>
                        {currentStep === 1 ? 'Download and Upload' : currentStep === 2 ? 'Data Validation' : 'Complete'}
                    </h4>
                    <div style={{ display: 'flex', gap: 10, opacity: isDisabled ? 0.6 : 1 }}>
                        {/* Discard */}
                        <button type="button" className="dark-button" onClick={handleDiscard}
                            disabled={isDisabled}
                            style={{ ...btnBase, ...discardStyle }}>
                            Discard
                        </button>

                        {/* Back */}
                        {currentStep > 1 && (
                            <button type="button" className="dark-button" onClick={handleBack}
                                disabled={isDisabled}
                                style={{ ...btnBase, ...backStyle }}>
                                Back
                            </button>
                        )}

                        {/* Next / Complete */}
                        {currentStep < 3
                            ? <button type="button" className="dark-border" onClick={handleNext}
                                disabled={isDisabled}
                                style={{ ...btnBase, ...nextStyle }}>
                                Next
                              </button>
                            : <button type="button" className="dark-border" onClick={handleCompleteClick}
                                disabled={completeDisabled}
                                style={{
                                    ...btnBase, ...completeStyle,
                                    cursor: completeDisabled ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s ease',
                                }}>
                                {isCompleting ? 'Completing...' : 'Save'}
                                {isCompleting && <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid #CED4DA', borderTopColor: '#6C757D', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />}
                              </button>
                        }
                    </div>
                </div>

                {/* ── Card Body — step 1 + step 2 scanning/valid ── */}
                {currentStep < 3 && !(currentStep === 2 && !isScanning && validationStatus === 'invalid') && (
                    <div style={{ background: '#FFF', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.10)', padding: '24px' }}>

                        {/* STEP 2: Scanning */}
                       
                             {currentStep === 2 && isScanning && <div>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, border: '1px solid #FFEB00', borderRadius: 12, background: '#FFFEF0', padding: '16px', marginBottom: 20 }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M12.0004 8.99998V13M12.0004 17H12.0104M21.7304 18L13.7304 3.99998C13.556 3.69218 13.303 3.43617 12.9973 3.25805C12.6917 3.07993 12.3442 2.98608 11.9904 2.98608C11.6366 2.98608 11.2892 3.07993 10.9835 3.25805C10.6778 3.43617 10.4249 3.69218 10.2504 3.99998L2.25042 18C2.0741 18.3053 1.98165 18.6519 1.98243 19.0045C1.98321 19.3571 2.0772 19.7032 2.25486 20.0078C2.43253 20.3124 2.68757 20.5646 2.99411 20.7388C3.30066 20.9131 3.64783 21.0032 4.00042 21H20.0004C20.3513 20.9996 20.6959 20.9069 20.9997 20.7313C21.3035 20.5556 21.5556 20.3031 21.7309 19.9991C21.9062 19.6951 21.9985 19.3504 21.9984 18.9995C21.9983 18.6486 21.9059 18.3039 21.7304 18Z" stroke="#FFC107" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                        </svg>
                                    <div>
                                        <div style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: 16, color: '#212529', marginBottom: 10 }}>Do not close or leave this page</div>
                                        <div style={{ fontFamily: 'Montserrat',fontWeight: 400,  fontSize: 14, color: '#212529' }}> Your file is being scanned. Navigating away or closing this page will interrupt the process and you will need to re-upload your file.</div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 0 40px' }}>
                                    <style>{`@keyframes erp-spin { to { transform: rotate(360deg); } } .erp-scanner-ring { width: 88px; height: 88px; border-radius: 50%; border: 8px solid #E9ECEF; border-top-color: #FFEB00; animation: erp-spin 0.9s linear infinite; margin-bottom: 24px; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
                                    <div className="erp-scanner-ring" />
                                    <h5 style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: 20, color: '#212529', margin: '0 0 10px' }}>Scanning your file for issues...</h5>
                                    <p style={{ fontFamily: 'Montserrat', fontSize: 14, color: '#6C757D', margin: 0 }}>This usually take a few seconds.</p>
                                </div>
                            </div>}
                        

                        {/* STEP 2: All Rows Valid */}
                        {currentStep === 2 && !isScanning && !errorModal.show && validationStatus === 'valid' && (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 20px' }}>
                                <div style={{ marginBottom: 24 }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="101" height="106" viewBox="0 0 101 106" fill="none">
                                        <circle cx="50.1404" cy="55.3594" r="50.1404" fill="#F8F9FA"/>
                                        <path d="M78.7716 96.0617H23.7603C22.0578 96.0617 20.6777 94.6816 20.6777 92.9792V17.7398C20.6777 16.0373 22.0578 14.6572 23.7603 14.6572H61.782L81.8542 34.619V92.9792C81.8542 94.6816 80.4741 96.0617 78.7716 96.0617Z" fill="#FFEB00"/>
                                        <path d="M81.8542 34.619L61.782 14.6572H23.7603C22.0578 14.6572 20.6777 16.0373 20.6777 17.7398V92.9792C20.6777 94.6816 22.0578 96.0617 23.7603 96.0617H78.7716C80.4741 96.0617 81.8542 94.6816 81.8542 92.9792V34.619ZM61.782 14.6572V31.5365C61.782 33.2389 63.1621 34.619 64.8646 34.619H81.8542" stroke="black" strokeWidth="2" strokeLinecap="round"/>
                                        <path d="M27.6992 27.6348H54.758" stroke="black" strokeWidth="1.5"/>
                                        <path d="M27.6992 41.792H39.4639" stroke="black" strokeWidth="1.5"/>
                                        <path d="M27.6992 34.7134H54.758" stroke="black" strokeWidth="1.5"/>
                                        <ellipse cx="42.405" cy="58.8989" rx="2.94118" ry="2.94944" fill="black"/>
                                        <ellipse cx="60.0525" cy="58.8989" rx="2.94118" ry="2.94944" fill="black"/>
                                        <path d="M45.3467 68.927C48.9659 71.1047 53.4921 71.1047 57.1114 68.927" stroke="black" strokeWidth="1.5" strokeLinecap="round"/>
                                        <path d="M78.1133 72.5C86.6589 72.5 93.5869 79.448 93.5869 88.0176C93.5867 96.587 86.6587 103.534 78.1133 103.534C69.568 103.534 62.6409 96.5869 62.6406 88.0176C62.6406 79.4481 69.5678 72.5002 78.1133 72.5Z" fill="black"/>
                                        <path d="M87.0753 82.4016L75.0191 94.4578L69.5391 88.9777" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M7.69922 15.8372H14.758" stroke="black" strokeLinecap="round"/>
                                        <path d="M22.9932 0.5V7.57865" stroke="black" strokeLinecap="round"/>
                                        <path d="M10.6406 3.44946L15.632 8.45483" stroke="black" strokeLinecap="round"/>
                                    </svg>
                                </div>
                                <h5 style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: 20, color: '#212529', margin: '0 0 8px' }}>All Rows are Valid</h5>
                                <p style={{ fontFamily: 'Montserrat', fontSize: 14, color: '#6C757D', margin: '0 0 28px' }}>You can proceed to the next step.</p>
                                <button className="dark-border" type="button" onClick={handleValidationDownload} disabled={isValidationDownloading}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 8, fontFamily: 'Montserrat', fontSize: 12, fontWeight: 600, cursor: isValidationDownloading ? 'not-allowed' : 'pointer', transition: 'all 0.2s ease', ...(isValidationDownloading ? { color: '#6C757D', border: '1px solid #CED4DA', background: '#E9ECEF' } : { background: '#212529', color: 'white', border: 'none' }) }}>
                                    Validation Result
                                    {isValidationDownloading ? <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid #CED4DA', borderTopColor: '#6C757D', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} /> : <Download size={16} />}
                                </button>
                            </div>
                        )}

                        {/* STEP 1 */}
                        {currentStep === 1 && (
                            <>
                                {formError && (
                                    <AlertNotification type="error" message={formError} recordName="" action="create" onClose={() => setFormError('')} />
                                )}

                                {/* Section 01 — Download Template */}
                                <div style={{ marginBottom: 28, borderLeft: '2px solid #FFEB00', paddingLeft: 16 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                        <span style={{ fontFamily: 'Montserrat', fontSize: 14, fontWeight: 600, color: '#ADB5BD' }}>01</span>
                                        <span style={{ fontFamily: 'Montserrat', fontSize: 12, fontWeight: 700, color: '#212529' }}>Download Template</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #CED4DA', borderRadius: 10, padding: '16px', background: '#F8F9FA' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                            <div style={{ width: 40, height: 40, background: '#FFF', borderRadius: '80px', flexShrink: 0, padding: '5px 11px 10px 4px', border: '1px solid #979593', aspectRatio: '1 / 1' }}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 23 23" fill="none">
                                                    <path d="M16.0929 1.67712H6.46842C6.20378 1.67712 5.98926 1.89165 5.98926 2.15629V20.8438C5.98926 21.1084 6.20378 21.323 6.46842 21.323H20.3643C20.6289 21.323 20.8434 21.1084 20.8434 20.8438V6.42768C20.8434 6.3006 20.7929 6.17873 20.7031 6.08886L16.4317 1.81747C16.3418 1.7276 16.2199 1.67712 16.0929 1.67712Z" fill="white" stroke="#979593" strokeWidth="2" strokeMiterlimit="10" />
                                                    <path d="M2.87533 17.7292H10.542C11.0713 17.7292 11.5003 17.3001 11.5003 16.7709V9.10421C11.5003 8.57494 11.0713 8.14587 10.542 8.14587H2.87533C2.34606 8.14587 1.91699 8.57494 1.91699 9.10421V16.7709C1.91699 17.3001 2.34606 17.7292 2.87533 17.7292Z" fill="#107C41" />
                                                    <path d="M4.06055 15.8125L5.94961 12.9294L4.21941 10.0625H5.61175L6.55629 11.895C6.64309 12.0687 6.70274 12.1985 6.73528 12.284H6.74745C6.81015 12.145 6.8752 12.01 6.94305 11.8791L7.9526 10.0625H9.23078L7.4559 12.9135L9.2757 15.8125H7.91587L6.82488 13.7996C6.77879 13.7249 6.73528 13.6353 6.69457 13.5309H6.67843C6.65948 13.5952 6.61736 13.6821 6.55231 13.7915L5.42857 15.8125H4.06055Z" fill="white" />
                                                </svg>
                                            </div>
                                            <div>
                                                <div style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: 12, color: '#212529' }}>Pricing Template</div>
                                                <div style={{ fontFamily: 'Montserrat', fontWeight: 400, fontSize: 14, color: '#212529', marginTop: 2 }}>Download the latest Pricing Template with all Active Listing Items</div>
                                            </div>
                                        </div>
                                        <button className="dark-border" type="button" onClick={handleDownload}
                                            onMouseEnter={() => !isDownloading && setIsDownloadHovered(true)}
                                            onMouseLeave={() => setIsDownloadHovered(false)}
                                            disabled={isDownloading}
                                            style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap', padding: 10, height: '40px', minWidth: '110px', cursor: isDownloading ? 'not-allowed' : 'pointer', transition: 'all 0.2s ease', ...(isDownloading ? { color: '#6C757D', border: '1px solid #CED4DA', background: '#E9ECEF', boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)' } : isDownloadHovered ? { color: '#212529', border: '1px solid #212529', background: '#FFF', boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)' } : {}) }}>
                                            Download
                                            {isDownloading ? <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid #CED4DA', borderTopColor: '#6C757D', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} /> : <Download size={16} />}
                                        </button>
                                    </div>
                                </div>

                                {/* Section 02 — Upload File */}
                                <div style={{ marginBottom: 28, borderLeft: '2px solid #FFEB00', paddingLeft: 16 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                        <span style={{ fontFamily: 'Montserrat', fontSize: 14, fontWeight: 600, color: '#ADB5BD' }}>02</span>
                                        <span style={{ fontFamily: 'Montserrat', fontSize: 12, fontWeight: 700, color: '#212529' }}>Upload File <span style={{ color: '#DC3545' }}>*</span></span>
                                    </div>
                                    <p style={{ fontFamily: 'Montserrat', fontSize: 14, color: '#212529', marginBottom: 12, marginLeft: 0, fontWeight: 400 }}>
                                        Upload the Pricing Excel with adjusted Base Price and Deductions
                                    </p>
                                    <div
                                        style={{ border: `1.5px dashed ${isDragging ? '#212529' : hasUploadError ? '#DC3545' : '#CED4DA'}`, borderRadius: 12, padding: '16px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: uploadedFile ? '120px' : 'auto', minHeight: '120px', cursor: uploadedFile ? 'default' : 'pointer', background: isDragging ? '#F8F9FA' : 'white', transition: 'border-color 0.2s, background 0.2s', position: 'relative', boxSizing: 'border-box' }}
                                        onClick={!uploadedFile ? handleBrowseClick : undefined}
                                        onDragOver={handleDragOver} onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDrop={handleDrop}
                                    >
                                        <input type="file" accept=".xls,.xlsx" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileChange} />
                                        {uploadedFile ? (
                                            <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                                                <div style={{ position: 'absolute', top: 0, right: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <button type="button" onClick={(e) => { e.stopPropagation(); handleRemoveFile(); fileInputRef.current && fileInputRef.current.click(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6C757D', padding: 0, display: 'flex', alignItems: 'center' }}>
                                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M11.5 2.5a1.414 1.414 0 0 1 2 2L5 13H3v-2L11.5 2.5Z" stroke="#6C757D" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none" /></svg>
                                                    </button>
                                                    <button type="button" onClick={(e) => { e.stopPropagation(); handleRemoveFile(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6C757D', fontSize: 18, lineHeight: 1, padding: 0, display: 'flex', alignItems: 'center' }}>×</button>
                                                </div>
                                                <svg width="40" height="48" viewBox="0 0 40 48" fill="none">
                                                    <path d="M4 0 H28 L40 12 V44 Q40 48 36 48 H4 Q0 48 0 44 V4 Q0 0 4 0 Z" fill="white" stroke="#E0E0E0" strokeWidth="1.5" />
                                                    <path d="M28 0 L40 12 H32 Q28 12 28 8 Z" fill="#E0E0E0" />
                                                    <rect x="0" y="22" width="28" height="20" rx="3" fill="#107C41" />
                                                    <text x="4" y="37" fontFamily="Arial, sans-serif" fontSize="14" fontWeight="700" fill="white">X</text>
                                                    <line x1="16" y1="6" x2="24" y2="6" stroke="#E0E0E0" strokeWidth="1.5" strokeLinecap="round" />
                                                    <line x1="8" y1="16" x2="26" y2="16" stroke="#E0E0E0" strokeWidth="1.5" strokeLinecap="round" />
                                                    <line x1="8" y1="20" x2="22" y2="20" stroke="#E0E0E0" strokeWidth="1.5" strokeLinecap="round" />
                                                </svg>
                                                <span style={{ fontFamily: 'Montserrat', fontSize: 13, color: '#212529' }}>{uploadedFile.name} ({(uploadedFile.size / (1024 * 1024)).toFixed(1)}MB)</span>
                                            </div>
                                        ) : (
                                            <>
                                                <CloudUpload size={24} color= 'black' style={{ marginBottom: 8 }} />
                                                <p style={{ fontFamily: 'Montserrat', fontSize: 12, color:'#6C757D', margin: 0, fontWeight: 400 }}>
                                                    Drag your Excel (.xlsx) here or{' '}
                                                    <span style={{ color: '#0D6EFD', cursor: 'pointer', fontWeight: 600,textDecoration:'underline' }}>Browse</span>
                                                </p>
                                            </>
                                        )}
                                    </div>
                                    {uploadError && <p style={{ color: '#DC3545', fontFamily: 'Montserrat', fontSize: 12, marginTop: 8 }}>{uploadError}</p>}
                                    {fieldErrors.uploadedFile && !uploadError && <p style={{ color: '#DC3545', fontFamily: 'Montserrat', fontSize: 12, marginTop: 8 }}>{fieldErrors.uploadedFile}</p>}
                                </div>

                                {/* Section 03 — Select Pricing Type */}
                                <div style={{ marginBottom: 28, borderLeft: '2px solid #FFEB00', paddingLeft: 16 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                        <span style={{ fontFamily: 'Montserrat', fontSize: 14, fontWeight: 600, color: '#ADB5BD' }}>03</span>
                                        <span style={{ fontFamily: 'Montserrat', fontSize: 12, fontWeight: 700, color: '#212529' }}>Select Pricing Type <span style={{ color: '#DC3545' }}>*</span></span>
                                    </div>
                                    <div style={{ display: 'flex', gap: 16 }}>
                                        {[
                                            { key: 'normal', label: 'Normal Pricing', desc: 'Set Default Effective Base Price and Deductions' },
                                            { key: 'promotional', label: 'Promotional Pricing', desc: 'Set Period-Specific Effective Base Price and Deductions' },
                                        ].map(({ key, label, desc }) => (
                                            <div key={key}
                                                style={{ flex: 1, border: `1px solid ${fieldErrors.pricingType ? '#FA5252' : '#CED4DA'}`, borderRadius: 12, padding: '16px 20px', cursor: 'pointer', background: '#FFF' }}
                                                onClick={() => { setPricingType(key); setFieldErrors((prev) => ({ ...prev, pricingType: '' })); }}>
                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                                                    <div style={{ width: 22, height: 22, borderRadius: '50%', border: `2px solid ${pricingType === key ? '#FFEB00' : '#212529'}`, background: pricingType === key ? '#FFEB00' : '#FFF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2, boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)' }}>
                                                        {pricingType === key && <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#212529' }} />}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: 14, color: '#212529', lineHeight: '20px' }}>{label}</div>
                                                        <div style={{ fontFamily: 'Montserrat', fontWeight: 400, fontSize: 12, color: '#6C757D', marginTop: 4 }}>{desc}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {fieldErrors.pricingType && <p style={{ color: '#FA5252', fontFamily: 'Montserrat', fontSize: 12, marginTop: 8 }}>{fieldErrors.pricingType}</p>}

                                    {pricingType === 'promotional' && (
                                        <div style={{ display: 'flex', gap: 16, marginTop: 20 }}>
                                            <div style={{ flex: 1 }}>
                                                <label style={{ fontFamily: 'Montserrat', fontSize: 12, fontWeight: 600, color: '#212529', display: 'block', marginBottom: 6 }}>Campaign Name <span style={{ color: '#DC3545' }}>*</span></label>
                                                <input
                                                    type="text"
                                                    value={campaignName}
                                                    onChange={(e) => { setCampaignName(e.target.value); setFieldErrors((prev) => ({ ...prev, campaignName: '' })); }}
                                                    onBlur={async () => {
                                                        if (!campaignName.trim()) return;
                                                        try {
                                                            const checkRes = await apiHelper(
                                                                `sales/check-campaign-name/?name=${encodeURIComponent(campaignName.trim())}`,
                                                                'GET'
                                                            );
                                                            if (checkRes && checkRes.ok) {
                                                                const checkData = await checkRes.json();
                                                                if (checkData.campaign_exists) {
                                                                    setFieldErrors((prev) => ({ ...prev, campaignName: 'Campaign Name already exists' }));
                                                                }
                                                            }
                                                        } catch (_) {}
                                                    }}
                                                    style={{ width: '100%', height: 40, padding: '0 14px', border: `1px solid ${fieldErrors.campaignName ? '#DC3545' : '#6C757D'}`, borderRadius: 8, fontFamily: 'Montserrat', fontSize: 12, outline: 'none', boxSizing: 'border-box', color: '#212529' }}
                                                />
                                                {fieldErrors.campaignName && <p style={{ color: '#DC3545', fontFamily: 'Montserrat', fontSize: 12, marginTop: 4 }}>{fieldErrors.campaignName}</p>}
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <label style={{ fontFamily: 'Montserrat', fontSize: 12, fontWeight: 600, color: '#212529', display: 'block', marginBottom: 6 }}>Promotional Period <span style={{ color: '#DC3545' }}>*</span></label>
                                                <div style={{ position: 'relative' }}>
                                                    <CalendarRangeInput name="promotionalPeriod" startDate={startTime} endDate={endTime} enableTime borderColor="#6C757D"
                                                        onChange={(e) => { setStartTime(e.target.value.start); setEndTime(e.target.value.end); setFieldErrors((prev) => ({ ...prev, promotionalPeriod: '' })); }} />
                                                    {fieldErrors.promotionalPeriod && <div style={{ position: 'absolute', inset: 0, borderRadius: 6, border: '1px solid #DC3545', pointerEvents: 'none' }} />}
                                                </div>
                                                {fieldErrors.promotionalPeriod && <p style={{ color: '#DC3545', fontFamily: 'Montserrat', fontSize: 12, marginTop: 4 }}>{fieldErrors.promotionalPeriod}</p>}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* ── STEP 2: Invalid Rows — two independent cards ── */}
                {currentStep === 2 && !isScanning && validationStatus === 'invalid' && !errorModal.show && (
                    <>
                        {/* Summary bar */}
                        <div style={{ background: '#FFF', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.10)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '20px', marginBottom: '12px' }}>
                            <button className="dark-border" type="button" onClick={handleValidationDownload} disabled={isValidationDownloading}
                                style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap', cursor: isValidationDownloading ? 'not-allowed' : 'pointer', transition: 'all 0.2s ease', ...(isValidationDownloading ? { color: '#6C757D', border: '1px solid #CED4DA', background: '#E9ECEF', boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)' } : { background: '#212529', color: 'white' }) }}>
                                Validation Result
                                {isValidationDownloading ? <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid #CED4DA', borderTopColor: '#6C757D', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} /> : <Download size={16} />}
                            </button>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid #E9ECEF', borderRadius: 8, padding: '8px 14px', fontFamily: 'Montserrat', fontSize: 12 }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M18.1085 14.9999L11.4419 3.33319C11.2965 3.0767 11.0857 2.86335 10.831 2.71492C10.5762 2.56649 10.2867 2.48828 9.99185 2.48828C9.69703 2.48828 9.40748 2.56649 9.15275 2.71492C8.89802 2.86335 8.68721 3.0767 8.54185 3.33319L1.87519 14.9999C1.72825 15.2543 1.65121 15.5431 1.65186 15.837C1.65251 16.1308 1.73083 16.4192 1.87889 16.673C2.02695 16.9269 2.23948 17.137 2.49493 17.2822C2.75039 17.4274 3.03969 17.5025 3.33352 17.4999H16.6669C16.9593 17.4996 17.2465 17.4223 17.4996 17.2759C17.7527 17.1295 17.9629 16.9191 18.1089 16.6658C18.255 16.4125 18.3319 16.1252 18.3318 15.8328C18.3317 15.5404 18.2547 15.2531 18.1085 14.9999Z" fill="#FFEB00"/>
                                    <path d="M10.0002 7.49986V10.8332V7.49986Z" fill="#FFEB00"/>
                                    <path d="M10.0002 14.1665H10.0085H10.0002Z" fill="#FFEB00"/>
                                    <path d="M10.0002 7.49986V10.8332M10.0002 14.1665H10.0085M18.1085 14.9999L11.4419 3.33319C11.2965 3.0767 11.0857 2.86335 10.831 2.71492C10.5762 2.56649 10.2867 2.48828 9.99185 2.48828C9.69703 2.48828 9.40748 2.56649 9.15275 2.71492C8.89802 2.86335 8.68722 3.0767 8.54185 3.33319L1.87519 14.9999C1.72825 15.2543 1.65121 15.5431 1.65186 15.837C1.65251 16.1308 1.73083 16.4192 1.87889 16.673C2.02695 16.9269 2.23948 17.137 2.49493 17.2822C2.75039 17.4274 3.03969 17.5025 3.33352 17.4999H16.6669C16.9593 17.4996 17.2465 17.4223 17.4996 17.2759C17.7527 17.1295 17.9629 16.9191 18.1089 16.6658C18.255 16.4125 18.3319 16.1252 18.3318 15.8328C18.3317 15.5404 18.2547 15.2531 18.1085 14.9999Z" stroke="#212529" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                                <span style={{ color: '#212529' }}>Warning: </span><strong style={{ color: '#212529' }}>{warningRows.length}</strong>
                                <span style={{ color: '#212529' }}>(Keep: <strong style={{ color: '#212529' }}>{warningKeep}</strong> ; Skip: <strong style={{ color: '#212529' }}>{warningSkip}</strong>)</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid #E9ECEF', borderRadius: 8, padding: '8px 14px', fontFamily: 'Montserrat', fontSize: 12 }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                <path d="M3.33325 11.6667C3.17555 11.6673 3.02094 11.623 2.88737 11.5392C2.75381 11.4554 2.64677 11.3354 2.5787 11.1931C2.51063 11.0509 2.48432 10.8922 2.50283 10.7356C2.52133 10.579 2.5839 10.4309 2.68325 10.3084L10.9332 1.80839C10.9951 1.73696 11.0795 1.68868 11.1724 1.6715C11.2653 1.65431 11.3613 1.66923 11.4447 1.71381C11.528 1.75838 11.5937 1.82997 11.631 1.91681C11.6683 2.00365 11.675 2.10059 11.6499 2.19172L10.0499 7.20839C10.0027 7.33466 9.98689 7.47049 10.0037 7.60423C10.0206 7.73797 10.0696 7.86562 10.1467 7.97624C10.2237 8.08686 10.3264 8.17715 10.446 8.23935C10.5656 8.30156 10.6985 8.33383 10.8332 8.33339H16.6666C16.8243 8.33285 16.9789 8.37707 17.1124 8.46091C17.246 8.54474 17.3531 8.66476 17.4211 8.80701C17.4892 8.94926 17.5155 9.1079 17.497 9.26451C17.4785 9.42111 17.4159 9.56926 17.3166 9.69172L9.06658 18.1917C9.00469 18.2632 8.92036 18.3114 8.82743 18.3286C8.73449 18.3458 8.63847 18.3309 8.55514 18.2863C8.4718 18.2417 8.40609 18.1701 8.3688 18.0833C8.33151 17.9965 8.32485 17.8995 8.34991 17.8084L9.94991 12.7917C9.99709 12.6655 10.0129 12.5296 9.99609 12.3959C9.97924 12.2621 9.9302 12.1345 9.85317 12.0239C9.77614 11.9132 9.67343 11.823 9.55385 11.7608C9.43426 11.6985 9.30137 11.6663 9.16658 11.6667H3.33325Z" fill="#FFC107" stroke="#212529" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                                <span style={{ color: '#212529' }}>Critical: </span><strong style={{ color: '#212529' }}>{criticalRows.length}</strong>
                                <span style={{ color: '#212529' }}>(Keep: <strong style={{ color: '#212529' }}>{criticalKeep}</strong> ; Skip: <strong style={{ color: '#212529' }}>{criticalSkip}</strong>)</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid #E9ECEF', borderRadius: 8, padding: '8px 14px', fontFamily: 'Montserrat', fontSize: 12 }}>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="0.5" y="0.5" width="19" height="19" rx="9.5" fill="#DC3545"/>
                                <rect x="0.5" y="0.5" width="19" height="19" rx="9.5" stroke="black"/>
                                <path d="M13.75 6.25L6.25 13.75M6.25 6.25L13.75 13.75" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>

                                <span style={{ color: '#212529' }}>Fatal: </span><strong style={{ color: '#212529' }}>{fatalRows.length}</strong>
                                <span style={{ color: '#212529' }}>(Skip <strong style={{ color: '#212529' }}>All</strong> by default)</span>
                            </div>
                        </div>

                        {/* Filter + table card */}
                        <div style={{ background: '#FFF', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.10)', overflow: 'hidden', padding: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    {['Warning', 'Critical', 'Fatal'].map((tab) => (
                                        <button key={tab} type="button" onClick={() => handleFilterChange(tab)}
                                            style={{ padding: '6px 16px', borderRadius: 20, border: activeFilter === tab ? 'none' : '1px solid #CED4DA', background: activeFilter === tab ? YELLOW : 'white', color: '#212529', fontFamily: 'Montserrat', fontSize: 12, fontWeight: activeFilter === tab ? 700 : 400, cursor: 'pointer' }}>
                                            {tab}
                                        </button>
                                    ))}
                                </div>
                                {!isFatal && currentFilterRows.length > 0 && (
                                    <button type="button" onClick={handleKeepAll} style={{ display: 'flex',justifyContent: 'center', alignItems: 'center', gap: 5, padding: '10px 10px 10px 5px', borderRadius: 8, border: '1px solid #212529', background: 'white', color: '#212529', fontFamily: 'Montserrat', fontSize: 12, fontWeight: 600, width: 110, height: 34, cursor: 'pointer' }}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
  <path d="M3 16.5H13.5C13.8978 16.5 14.2794 16.342 14.5607 16.0607C14.842 15.7794 15 15.3978 15 15V5.25L11.25 1.5H4.5C4.10218 1.5 3.72064 1.65804 3.43934 1.93934C3.15804 2.22064 3 2.60218 3 3V6M10.5 1.5V4.5C10.5 4.89782 10.658 5.27936 10.9393 5.56066C11.2206 5.84196 11.6022 6 12 6H15M1.5 11.25H9M6.75 9L9 11.25L6.75 13.5" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg> Keep All
                                    </button>
                                )}
                            </div>

                            {currentFilterRows.length === 0 && (
                                <div className="empty-state">
                                    <div className="empty-state-content">
                                        <div className="icon">
                                            <img src={noBrandImage} alt="empty" style={{ width: 120, height: 120 }} />
                                        </div>
                                        <h1 className="no-item-title mt-2">No Invalid Rows Found</h1>
                                        <p className="no-item-text">Nothing here yet</p>
                                    </div>
                                </div>
                            )}

                            {currentFilterRows.length > 0 && (
                                <div>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Montserrat' }}>
                                        <thead>
                                            <tr style={{ background: '#E9ECEF' }}>
                                                <th onClick={() => handleValidationSort('row')} style={{ height: 45, padding: '10px', width: 100, whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}>
                                                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'Montserrat', fontSize: 12, fontWeight: 700, color: '#212529' }}>
                                                        Row #
                                                        <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                                            <svg width={12} height={12} viewBox="0 0 24 24" fill={validationSortField === 'row' && validationSortOrder === 'asc' ? '#212529' : '#6C757D'}><path d="M12 8l6 6H6l6-6z" /></svg>
                                                            <svg style={{marginTop: '-7px'}}width={12} height={12} viewBox="0 0 24 24" fill={validationSortField === 'row' && validationSortOrder === 'desc' ? '#212529' : '#6C757D'}><path d="M12 16l-6-6h12l-6 6z" /></svg>
                                                        </span>
                                                    </span>
                                                </th>
                                                <th style={{ height: 45, padding: '10px', fontFamily: 'Montserrat', fontSize: 12, fontWeight: 700, color: '#212529', textAlign: 'left' }}>Issue Details</th>
                                                <th style={{ height: 45, padding: '10px', fontFamily: 'Montserrat', fontSize: 12, fontWeight: 700, color: '#212529', textAlign: 'left', width: 160 }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {pagedRows.map((row, idx) => (
                                                <tr key={row.id} style={{ borderTop: '1px solid #E9ECEF', background: idx % 2 === 1 ? '#F8F9FB' : 'white' }}>
                                                    <td style={{ padding: '10px', fontSize: 12, color: '#212529' }}>{row.row}</td>
                                                    <td style={{ padding: '10px', fontSize: 12, color: '#212529' }}>
                                                        {Array.isArray(row.issues)
                                                            ? row.issues.map((msg, i) => (
                                                                <div key={i} style={{ paddingBottom: i < row.issues.length - 1 ? 6 : 0, borderBottom: i < row.issues.length - 1 ? '1px dashed #E9ECEF' : 'none', marginBottom: i < row.issues.length - 1 ? 6 : 0 }}>{msg}</div>
                                                              ))
                                                            : row.issues}
                                                    </td>
                                                    <td style={{ padding: '10px' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: isFatal ? 'not-allowed' : 'pointer', fontFamily: 'Montserrat', fontSize: 12, color:  '#212529', margin: 0 }}>
                                                                <div onClick={!isFatal ? () => updateRowAction(row.id, 'keep') : undefined} style={{ width: 18, height: 18, borderRadius: '50%', border: isFatal ? '1px solid #ADB5BD' : '1px solid #212529', background: isFatal ? '#E9ECEF' : row.action === 'keep' ? YELLOW : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: isFatal ? 'not-allowed' : 'pointer', flexShrink: 0 }}>
                                                                    {!isFatal && row.action === 'keep' && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#212529' }} />}
                                                                </div>
                                                                Keep
                                                            </label>
                                                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: isFatal ? 'not-allowed' : 'pointer', fontFamily: 'Montserrat', fontSize: 12, color:  '#212529', margin: 0}}>
                                                                <div onClick={!isFatal ? () => updateRowAction(row.id, 'skip') : undefined} style={{ width: 18, height: 18, borderRadius: '50%', background:  row.action === 'skip' ? YELLOW : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: isFatal ? 'not-allowed' : 'pointer', flexShrink: 0,border: row.action !== 'skip' ? '1px solid #212529':'' }}>
                                                                    {(isFatal || row.action === 'skip') && <div style={{ width: 8, height: 8, borderRadius: '50%', background:  '#212529' }} />}
                                                                </div>
                                                                Skip
                                                            </label>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    <div style={{ borderTop: '1px solid #E9ECEF', padding: '12px 16px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
                                        <span style={{ fontFamily: 'Montserrat', fontSize: 13, color: '#6C757D', whiteSpace: 'nowrap' }}>
                                            {(validationPage - 1) * PAGE_SIZE + 1} - {Math.min(validationPage * PAGE_SIZE, sortedFilterRows.length)} of {sortedFilterRows.length}
                                        </span>
                                        <Pagination currentPage={validationPage} totalPages={totalPages} onPageChange={setValidationPage} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {/* ── STEP 3: Summary + Notes ── */}
                {currentStep === 3 && (
                    <>
                        <div style={{ background: '#FFF', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.10)', overflow: 'hidden', marginBottom: '12px' }}>
                            <div style={{ padding: '15px 20px',marginBottom:'20px 0px',background:'#F8F9FA' }}>
                                <span style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: 14, color: '#212529' }}>Summary</span>
                            </div>
                            <div style={{padding:20}}>

                            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'Montserrat',borderBottom:' 1px solid var(--Gray-300, #DEE2E6)'}}>
                                <thead>
                                    <tr style={{ background: '#E9ECEF' }}>
                                        {['Final Pricing Rows', 'File Total Rows', 'File Updated Rows', 'Skipped Rows'].map((col) => (
                                            <th key={col} style={{ padding: '10px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#212529', borderTop: '1px solid #E9ECEF', borderBottom: '1px solid #E9ECEF' }}>{col}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr >
                                        <td style={{ padding: '10px', fontSize: 12, color: '#1F1F1F' }}>{summaryData.finalPricingRows}</td>
                                        <td style={{ padding: '10px', fontSize: 12, color: '#1F1F1F' }}>{summaryData.fileTotalRows}</td>
                                        <td style={{ padding: '10px', fontSize: 12, color: '#1F1F1F' }}>{summaryData.fileUpdatedRows}</td>
                                        <td style={{ padding: '10px', fontSize: 12, color: '#1F1F1F' }}>{summaryData.skippedRows}</td>
                                    </tr>
                                </tbody>
                            </table>
                            </div>
                        </div>
                        {/* <div style={{ background: '#FFF', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.10)', overflow: 'hidden' }}>
                            <div style={{ padding: '15px 20px',background:'#F8F9FA'}}>
                                <span style={{ fontFamily: 'Montserrat', fontWeight: 600, fontSize: 14, color: '#212529' }}>Notes</span>
                            </div>
                            <div style={{ padding: '20px' }}>
                                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={8}
                                    style={{ width: '100%', border: '1px solid #CED4DA', borderRadius: 8, padding: '10px', fontFamily: 'Montserrat', fontSize: 12, color: '#212529', resize: 'none', outline: 'none', boxSizing: 'border-box', boxShadow: 'none' }} />
                            </div>
                        </div> */}
                        {completeError && (
                            <div style={{ marginTop: 12 }}>
                                <AlertNotification type="error" message={completeError} recordName="" action="create" onClose={() => setCompleteError('')} />
                            </div>
                        )}
                    </>
                )}

            </div>

            <DiscardConfirmationModal
                isOpen={discardModalOpen}
                entityName="Trade-In Pricing"
                onCancel={handleCancelDiscard}
                onConfirm={handleConfirmDiscard}
            />

            {/* ── Error Modal ── */}
            {errorModal.show && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: 'white', borderRadius: 12, padding: '32px', width: 650, height: 222, position: 'relative', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', fontFamily: 'Montserrat', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <button type="button" onClick={closeErrorModal}
                            style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer', color: '#212529', fontSize: 20, lineHeight: 1, padding: 4 }}>
                            ×
                        </button>
                        <div>
                            <h4 style={{ margin: '0 0 12px', fontFamily: 'Montserrat', fontWeight: 600, fontSize: 20, color: '#212529' }}>{errorModal.title || 'No valid rows found'}</h4>
                            <p style={{ margin: 0, fontFamily: 'Montserrat', fontSize: 14, color: '#6C757D', lineHeight: '1.6' }}>{errorModal.message || 'The uploaded file is empty or missing the "Master List" sheet. Please check and upload again.'}</p>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button type="button" onClick={closeErrorModal}
                                style={{ background: '#212529', color: 'white', border: 'none', width:'100px', height:'34px', borderRadius: 8, padding: '10px 10px', fontFamily: 'Montserrat', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Campaign Start Time Confirmation Modal ── */}
            {campaignTimeModal.show && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{
                        maxWidth: 650,
                        width: 650,
                        background: 'white',
                        borderRadius: 12,
                        zIndex: 1050,
                        padding: '20px 25px',
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        boxShadow: '0px 2px 10px rgba(0,0,0,0.2)',
                        height: 'auto',
                        boxSizing: 'border-box',
                    }}>
                        {/* Close Button */}
                        <div className="d-flex justify-content-end align-items-center pt-0 pe-0">
                            <button
                                onClick={() => setCampaignTimeModal({ show: false })}
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', outline: 'none' }}
                                onFocus={(e) => e.target.blur()}
                            >
                                <svg width="18" height="18" viewBox="0 0 18 18">
                                    <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="black" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </button>
                        </div>

                        {/* Title */}
                        <h3 style={{
                            textAlign: 'left', color: '#212529',
                            fontSize: '20px', fontWeight: '600',
                            lineHeight: '24px', fontFamily: 'Montserrat',
                            marginBottom: '10px', paddingTop: '20px',
                        }}>
                            Past start time, pricing will take effect immediately
                        </h3>

                        {/* Message */}
                        <p style={{
                            paddingBottom: '20px',
                            color: '#6C757D',
                            fontFamily: 'Montserrat',
                            fontSize: '14px',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: '20px',
                            margin: 0,
                        }}>
                            Since the configured start time has passed, this promotional pricing will take effect as of the current time once saved.
                        </p>

                        {/* Footer Buttons */}
                        <div className="drawer-footer" style={{ paddingTop: '10px', paddingBottom: '10px' }}>
                            <button
                                onClick={() => setCampaignTimeModal({ show: false })}
                                style={{
                                    display: 'flex', height: '34px', minWidth: '100px',
                                    padding: '10px 0px', justifyContent: 'center',
                                    alignItems: 'center', gap: '10px', borderRadius: '8px',
                                    border: '1px solid var(--navbar-dropdown-active, #212529)',
                                    background: 'var(--Background-White, #FFF)',
                                    boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)',
                                    color: 'var(--navbar-dropdown-active, #212529)',
                                    fontFamily: 'Montserrat', fontSize: '12px',
                                    fontWeight: 600, fontStyle: 'normal', lineHeight: 'normal',
                                    cursor: 'pointer',
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleComplete}
                                style={{
                                    display: 'flex', height: '34px', minWidth: '100px',
                                    padding: '10px 0px', justifyContent: 'center',
                                    alignItems: 'center', gap: '10px', borderRadius: '8px',
                                    background: 'var(--navbar-dropdown-active, #212529)',
                                    boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)',
                                    color: 'var(--Background-White, #FFF)',
                                    fontFamily: 'Montserrat', fontSize: '12px',
                                    fontWeight: 600, fontStyle: 'normal', lineHeight: 'normal',
                                    border: 'none', cursor: 'pointer',
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Campaign End Time Passed Modal ── */}
            {campaignEndTimeModal.show && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{
                        maxWidth: 650,
                        width: 650,
                        background: 'white',
                        borderRadius: 12,
                        zIndex: 1050,
                        padding: '20px 25px',
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        boxShadow: '0px 2px 10px rgba(0,0,0,0.2)',
                        height: 'auto',
                        boxSizing: 'border-box',
                    }}>
                        {/* Close Button */}
                        <div className="d-flex justify-content-end align-items-center pt-0 pe-0">
                            <button
                                onClick={() => setCampaignEndTimeModal({ show: false })}
                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', outline: 'none' }}
                                onFocus={(e) => e.target.blur()}
                            >
                                <svg width="18" height="18" viewBox="0 0 18 18">
                                    <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="black" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </button>
                        </div>

                        {/* Title */}
                        <h3 style={{
                            textAlign: 'left', color: '#212529',
                            fontSize: '20px', fontWeight: '600',
                            lineHeight: '24px', fontFamily: 'Montserrat',
                            marginBottom: '10px', paddingTop: '20px',
                        }}>
                            Past end time, please reset the promotional period
                        </h3>

                        {/* Message */}
                        <p style={{
                            paddingBottom: '20px',
                            color: '#6C757D',
                            fontFamily: 'Montserrat',
                            fontSize: '14px',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: '20px',
                            margin: 0,
                        }}>
                            Since the configured end time has passed, this pricing cannot be saved. Please adjust the promotional period and try again.
                        </p>

                        {/* Footer — OK only */}
                        <div className="drawer-footer" style={{ paddingTop: '10px', paddingBottom: '10px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setCampaignEndTimeModal({ show: false })}
                                style={{
                                    display: 'flex', height: '34px', minWidth: '100px',
                                    padding: '10px 0px', justifyContent: 'center',
                                    alignItems: 'center', gap: '10px', borderRadius: '8px',
                                    background: 'var(--navbar-dropdown-active, #212529)',
                                    boxShadow: '0 1px 4px 0 rgba(0,0,0,0.10)',
                                    color: 'var(--Background-White, #FFF)',
                                    fontFamily: 'Montserrat', fontSize: '12px',
                                    fontWeight: 600, fontStyle: 'normal', lineHeight: 'normal',
                                    border: 'none', cursor: 'pointer',
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}